<template>
  <v-dialog v-model="dialog" max-width="560" scrollable>
    <v-card class="kf-utm-dialog">
      <v-card-title class="kf-utm-dialog__head">
        <div class="kf-utm-dialog__heading">
          <div class="kf-dialog__title">UTM Details</div>
          <!-- Says what these six values are, so the popup reads on its own
               without remembering which row opened it. -->
          <div class="kf-utm-dialog__sub">Campaign attribution for this record</div>
        </div>
        <v-spacer />
        <v-btn
          icon
          class="kf-utm-dialog__close"
          aria-label="Close"
          @click="dialog = false"
        >
          <LucideIcon name="x" :size="20" />
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="kf-utm-dialog__body">
        <!-- All six absent is the ordinary case for an organic signup. Six rows
             each reading "Not set" states that six times and fills the box; one
             line states it once. -->
        <div v-if="!hasAny" class="kf-utm-dialog__none">
          No UTM data recorded for this record.
        </div>

        <!-- One row per field, each row its own two-track grid: label in the
             left track, value in the right, never in the same box. Deliberately
             not a table -- the version this replaces was a v-simple-table whose
             th/td pairs ran together into "UTM Medium social". Grid items are
             blockified by the spec, so no inherited rule can put these two back
             on one line. -->
        <dl v-else class="kf-utm-dialog__list">
          <div v-for="f in fields" :key="f.label" class="kf-utm-dialog__row">
            <dt class="kf-utm-dialog__label">{{ f.label }}</dt>
            <dd
              class="kf-utm-dialog__value"
              :class="{ 'kf-utm-dialog__value--empty': f.empty }"
              :title="f.empty ? '' : String(f.value)"
            >{{ f.empty ? "Not set" : f.value }}</dd>
          </div>
        </dl>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * The UTM attribution popup, shared by eight screens.
 *
 * Opened imperatively -- `this.$refs.UtmDialog.openDialog(item)` -- from User
 * Management, Offer Management and Leads Management (a link in the UTM column)
 * and from Disbursement, LAS, Pre-verification, Business Loan and New Customer
 * (an icon button in the actions column). That contract is depended on by all
 * eight and must not change.
 *
 * Two payload shapes arrive. User Management and Offer Management pass the
 * normalised group built by utmDetails() in app-js/karrofin/api/normalise.js,
 * which fills every missing key with "". The other six pass the whole raw API
 * row, where a utm_* key may be absent entirely. Hence `empty` below tests for
 * "", null and undefined rather than just falsiness -- the two shapes disagree
 * about how an absent value is spelled.
 */

/**
 * The six fields, in the order the filter panels list them. Same keys and
 * labels as the UTM_FILTERS constants on Leads and User Management, so the
 * popup names a value the way the filter that found it does.
 */
const UTM_FIELDS = [
  { key: "utm_source", label: "Partner ID (utm_source)" },
  { key: "utm_medium", label: "Publisher (utm_medium)" },
  { key: "utm_campaign", label: "UTM Campaign" },
  { key: "utm_content", label: "Source (utm_content)" },
  { key: "utm_term", label: "UTM Term" },
  { key: "utm_platform", label: "UTM Platform" },
];

export default {
  name: "UtmDetailsDialog",

  data() {
    return {
      dialog: false,
      details: {},
    };
  },

  computed: {
    /** { label, value, empty } -- one row each. */
    fields() {
      const src = this.details || {};
      return UTM_FIELDS.map((f) => {
        const value = src[f.key];
        return {
          label: f.label,
          value: value,
          empty: value === "" || value === null || value === undefined,
        };
      });
    },

    hasAny() {
      return this.fields.some((f) => !f.empty);
    },
  },

  methods: {
    openDialog(item) {
      this.dialog = true;
      this.details = item || {};
    },
  },
};
</script>

<style scoped>
.kf-utm-dialog__head {
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px 12px;
}
.kf-utm-dialog__heading {
  min-width: 0;
}
/* The panel's dialog title -- the same rule as ProductFormDialog and
   CampaignUrlDialog. Repeated here rather than shared because <style> is
   scoped. `word-break` undoes Vuetify's break-all on .v-card__title. */
.kf-dialog__title {
  font-size: 17px;
  font-weight: 700;
  color: var(--kf-brand);
  line-height: 1.3;
  word-break: normal;
}
.kf-utm-dialog__sub {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  margin-top: 2px;
}
/* `.v-btn` in the selector so this outranks Vuetify's own button rules rather
   than relying on stylesheet order. Vuetify sets no background on an icon
   button, so the hover fill is ours to give. */
.kf-utm-dialog__close.v-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  /* Darker than the subtitle at rest, so the glyph is findable rather than
     something you have to go looking for. */
  color: var(--kf-text-body);
}
.kf-utm-dialog__close.v-btn:hover {
  background: var(--kf-hover);
  color: var(--kf-text);
}

/* The rows carry their own 12px side padding, so the body only supplies the
   top and bottom. Nesting one padding inside another is what made the text
   look squeezed into the middle of a large empty box. */
.kf-utm-dialog__body {
  padding: 8px 4px 12px;
}

.kf-utm-dialog__list {
  margin: 0;
}

/* minmax(0, 1fr) on the value track is what lets a long campaign string wrap
   instead of forcing the row wider than the dialog. */
.kf-utm-dialog__row {
  display: grid;
  grid-template-columns: minmax(120px, 34%) minmax(0, 1fr);
  gap: 8px;
  padding: 8px 12px;
  align-items: baseline;
  border-bottom: 1px solid var(--kf-border);
}
.kf-utm-dialog__row:last-child {
  border-bottom: none;
}

.kf-utm-dialog__label {
  font-size: 14px;
  font-weight: 600;
  color: var(--kf-text-muted);
  line-height: 1.4;
}
.kf-utm-dialog__value {
  font-size: 16px;
  font-weight: 600;
  color: var(--kf-text);
  line-height: 1.4;
  /* Browsers indent <dd> by 40px by default, which would offset the whole
     value column away from its track. */
  margin: 0;
  word-break: break-word;
}
/* An absent value is stated, not left blank -- but it recedes by weight and
   size tier rather than by losing contrast. */
.kf-utm-dialog__value--empty {
  font-weight: 400;
  color: var(--kf-text-muted);
}

/* Below this the label track would squeeze a value into ~150px, so the pair
   stacks instead of being crushed. */
@media (max-width: 480px) {
  .kf-utm-dialog__row {
    grid-template-columns: minmax(0, 1fr);
    gap: 2px;
  }
}

.kf-utm-dialog__none {
  font-size: var(--kf-fs-body);
  color: var(--kf-text-muted);
  padding: 8px 12px;
}

/* Only reachable if a campaign string is long enough to overflow. Kept subtle
   so it does not read as a second border. */
.kf-utm-dialog__body::-webkit-scrollbar {
  width: 8px;
}
.kf-utm-dialog__body::-webkit-scrollbar-thumb {
  background: var(--kf-border-strong);
  border-radius: 4px;
}
</style>
